import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import { RaceStatus as RaceStatusType } from '@ps/web-sports-api-types';
import classNames from 'clsx';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { IconName } from '@swf/sports/types/icon';

import { Icon } from '..';

import {
  TXTWCL_Abandoned,
  TXTWCL_At_The_Post,
  TXTWCL_Delayed,
  TXTWCL_False_Start,
  TXTWCL_Finished,
  TXTWCL_Going_Behind,
  TXTWCL_Going_Down,
  TXTWCL_Off_2,
  TXTWCL_Parading,
  TXTWCL_Photograph_1,
  TXTWCL_Race_Void,
  TXTWCL_Result_3,
  TXTWCL_Under_Orders,
  TXTWCL_Weighed_In
} from './RaceStatus.messages.json';
import styles from './RaceStatus.scss';

const statusMap: Record<
  Exclude<RaceStatusType, RaceStatusType.DORMANT>,
  { iconName?: IconName; className?: string; text: TranslationTag }
> = {
  [RaceStatusType.AT_THE_POST]: { iconName: 'horse', text: TXTWCL_At_The_Post },
  [RaceStatusType.DELAYED]: { iconName: 'horse', className: styles.delayed, text: TXTWCL_Delayed },
  [RaceStatusType.FALSE_START]: { iconName: 'horse', text: TXTWCL_False_Start },
  [RaceStatusType.FINISHED]: {
    iconName: 'finishingpost',
    className: styles.finished,
    text: TXTWCL_Finished
  },
  [RaceStatusType.GOING_BEHIND]: { iconName: 'horse', text: TXTWCL_Going_Behind },
  [RaceStatusType.GOING_DOWN]: { iconName: 'horse', text: TXTWCL_Going_Down },
  [RaceStatusType.OFF]: { iconName: 'horse', text: TXTWCL_Off_2 },
  [RaceStatusType.PARADING]: { iconName: 'horse', text: TXTWCL_Parading },
  [RaceStatusType.PHOTOGRAPH]: { iconName: 'horse', text: TXTWCL_Photograph_1 },
  [RaceStatusType.RESULT]: {
    iconName: 'finishingpost',
    className: styles.finished,
    text: TXTWCL_Result_3
  },
  [RaceStatusType.UNDER_ORDERS]: { iconName: 'horse', text: TXTWCL_Under_Orders },
  [RaceStatusType.WEIGHED_IN]: { iconName: 'horse', text: TXTWCL_Weighed_In },
  [RaceStatusType.RACE_VOID]: { text: TXTWCL_Race_Void, className: styles.raceVoid },
  [RaceStatusType.ABANDONED]: { text: TXTWCL_Abandoned, className: styles.abandoned }
};

interface Props {
  status: RaceStatusType;
  iconOnly?: true;
}

export const RaceStatus: React.FC<Props> = ({ status, iconOnly }) => {
  const { formatMessage } = useRegulatoryIntl();

  if (status === RaceStatusType.DORMANT) return null;

  const mappedStatus = statusMap[status];

  if (iconOnly && !mappedStatus.iconName) return null;

  return (
    <div className={classNames(styles.raceStatus, mappedStatus.className)}>
      {mappedStatus.iconName && (
        <Icon
          name={mappedStatus.iconName}
          label={iconOnly ? formatMessage(mappedStatus.text) : undefined}
          size="x2"
        />
      )}
      {!iconOnly && <Translation {...mappedStatus.text} />}
    </div>
  );
};

export default RaceStatus;
